import { useEffect, useRef, useState } from "react";
import { CalendarClock, Eye, ImagePlus, Languages, Loader2, MailCheck, Pencil, Plus, RefreshCw, Send, Sparkles, Square, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", categories: "", tags: "", image: "", lang: "it", visibility: "public", status: "published", publish_at: "", notify: true, post_id: "" };

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function AdminBlog() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState("");
  const [batch, setBatch] = useState(null);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/blog/posts").then(({ data }) => setPosts(data.data || [])).catch(() => setPosts([]));
  const loadStats = () => api.get("/admin/blog/stats").then(({ data }) => setStats(data.data || null)).catch(() => {});
  useEffect(() => { load(); loadStats(); }, []);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(lang, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  const startEdit = (p) => {
    setForm({
      title: p.title, slug: p.slug, excerpt: p.excerpt || "", content: p.content || "",
      categories: (p.categories || []).join(", "), tags: (p.tags || []).join(", "),
      image: p.image || "", lang: p.lang || "it",
      visibility: p.visibility || "public", status: p.status || "published",
      publish_at: toLocalInput(p.publish_at), notify: p.notify !== false, post_id: p.post_id,
    });
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/blog/posts", {
        ...form,
        categories: form.categories.split(",").map((c) => c.trim()).filter(Boolean),
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : "",
      });
      toast.success(t("admin.saved"));
      setForm(EMPTY);
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const del = async (p) => {
    setBusy(true);
    try {
      await api.delete(`/admin/blog/posts/${p.post_id}`);
      toast.success(t("admin.saved"));
      await load();
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const notify = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/blog/posts/${p.post_id}/notify`);
      toast.success(t("blog.sentTo").replace("{n}", data.data.recipients));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const translate = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/blog/posts/${p.post_id}/translate`, {}, { timeout: 300000 });
      const d = data.data;
      if (d.created === 0) toast.info(t("blog.noTranslate"));
      else toast.success(t("blog.translatedOk").replace("{n}", d.created));
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      toast.error(detail === "budget_llm_esaurito" ? t("blog.aiBudget") : (detail || t("misc.error")));
    } finally {
      setBusy(false);
    }
  };

  const regen = async (p) => {
    setBusy(true);
    try {
      await api.post(`/admin/blog/posts/${p.post_id}/regenerate`, {}, { timeout: 300000 });
      toast.success(t("blog.regenOk"));
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      toast.error(detail === "budget_llm_esaurito" ? t("blog.aiBudget") : (detail || t("misc.error")));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/blog/upload", fd);
      setForm((f) => ({ ...f, image: data.data.url }));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setUploading(false);
    }
  };

  // ---- Assistente AI ----
  const aiDraft = async () => {
    if (!aiTopic.trim()) return;
    setAiBusy("draft");
    try {
      const { data } = await api.post("/admin/blog/ai-draft", { topic: aiTopic.trim(), lang: form.lang || "it" }, { timeout: 120000 });
      const d = data.data;
      setForm((f) => ({ ...f, title: d.title, excerpt: d.excerpt, content: d.content,
        categories: (d.categories || []).join(", "), tags: (d.tags || []).join(", "), slug: "" }));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setAiBusy("");
    }
  };

  const aiImage = async () => {
    const prompt = (form.title || aiTopic).trim();
    if (!prompt) return;
    setAiBusy("image");
    try {
      const { data } = await api.post("/admin/blog/ai-image", { prompt }, { timeout: 120000 });
      setForm((f) => ({ ...f, image: data.data.url }));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setAiBusy("");
    }
  };

  const loadBatch = () => api.get("/admin/blog/ai-batch").then(({ data }) => setBatch(data.data || null)).catch(() => {});
  const [blogAutoOn, setBlogAutoOn] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setBlogAutoOn(!!data.data?.blog_auto_enabled)).catch(() => {});
  }, []);
  useEffect(() => {
    loadBatch();
    const iv = setInterval(() => { if (batch?.status === "running") { loadBatch(); load(); } }, 10000);
    return () => clearInterval(iv);
  }, [batch?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const aiBatchStop = async () => {
    setAiBusy("stop");
    try {
      await api.post("/admin/blog/ai-batch/stop");
      toast.success(t("blog.aiBatchStopped"));
      loadBatch();
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setAiBusy("");
    }
  };

  const aiBatchStart = async () => {
    setAiBusy("batch");
    try {
      const { data } = await api.post("/admin/blog/ai-batch", { count: 100, with_images: true, auto: true }, { timeout: 30000 });
      setBatch(data.data);
      toast.success(t("blog.aiBatchStarted"));
    } catch (err) {
      toast.error(err.response?.data?.detail === "blog_auto_disabled" ? t("blog.aiAutoDisabled") : (err.response?.data?.detail || t("misc.error")));
    } finally {
      setAiBusy("");
    }
  };

  const inputCls = "w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors";
  const lbl = "font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 block mb-1.5";

  return (
    <div data-testid="admin-blog-page" className="mt-10 grid lg:grid-cols-[1fr_380px] gap-6 items-start">
      {/* lista articoli */}
      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6">
        <h2 className="font-display font-bold text-2xl text-slate-900 mb-1">Blog</h2>
        <p className="text-sm text-slate-500 mb-5">{t("blog.adminSub")}</p>
        {stats && (
          <div data-testid="admin-blog-stats" className="mb-5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-50 border border-slate-100 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {stats.posts} {t("blog.statsPosts")}
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-widest text-emerald-700">
              {stats.live} {t("blog.statsLive")}
            </span>
            <span className="rounded-full bg-violet-50 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-widest text-violet-700">
              {stats.scheduled} {t("blog.statsScheduled")}
            </span>
            <span className="rounded-full bg-orange-50 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] inline-flex items-center gap-1">
              <Eye className="w-3 h-3" /> {stats.views} {t("blog.statsViews")}
            </span>
            {(stats.top || []).length > 0 && (
              <span className="w-full mt-1 text-xs text-slate-400">
                {t("blog.topRead")}: {stats.top.slice(0, 3).map((x) => `${x.title} (${x.views})`).join(" · ")}
              </span>
            )}
          </div>
        )}
        {posts === null ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : posts.length === 0 ? (
          <p data-testid="admin-blog-empty" className="py-10 text-left text-sm text-slate-400">{t("blog.empty")}</p>
        ) : (
          <div className="space-y-2" data-testid="admin-blog-list">
            {posts.map((p) => (
              <div key={p.post_id} data-testid={`admin-blog-row-${p.post_id}`} className="flex items-center gap-3 rounded-2xl border border-slate-100 px-4 py-3 hover:bg-[#FAF5EE] transition-colors">
                {p.image && (
                  <img src={imgSrc(p.image)} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0 border border-slate-100" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{p.title}</div>
                  <div className="text-xs text-slate-400 font-mono-t truncate">
                    /blog/{p.slug} · {(p.categories || []).join(", ") || "—"} · {p.lang}
                    {(p.views || 0) > 0 && (
                      <span data-testid={`admin-blog-views-${p.post_id}`} className="ml-2 inline-flex items-center gap-0.5 text-slate-400">
                        <Eye className="w-3 h-3" /> {p.views}
                      </span>
                    )}
                  </div>
                  {p.scheduled && (
                    <div data-testid={`admin-blog-scheduled-${p.post_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-violet-600 font-medium">
                      <CalendarClock className="w-3.5 h-3.5" /> {t("blog.scheduledFor")} {fmtDate(p.publish_at)}
                    </div>
                  )}
                  {p.newsletter_sent_at && (
                    <div data-testid={`admin-blog-newsletter-${p.post_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium ml-3">
                      <MailCheck className="w-3.5 h-3.5" /> {t("blog.sentTo").replace("{n}", p.newsletter_recipients ?? 0)}
                    </div>
                  )}
                  {(p.translations || []).length > 0 && (
                    <div data-testid={`admin-blog-translations-${p.post_id}`} className="mt-1 inline-flex items-center gap-1 ml-3">
                      <Languages className="w-3.5 h-3.5 text-slate-400" />
                      {p.translations.map((l) => (
                        <span key={l} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono-t text-[9px] font-bold uppercase text-slate-500">{l}</span>
                      ))}
                    </div>
                  )}
                </div>
                <button data-testid={`admin-blog-translate-${p.post_id}`} onClick={() => translate(p)} disabled={busy} title={t("blog.translate")}
                  className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Languages className="w-3.5 h-3.5" />
                </button>
                {p.status === "published" && p.visibility === "public" && !p.newsletter_sent_at && !p.scheduled && (
                  <button data-testid={`admin-blog-notify-${p.post_id}`} onClick={() => notify(p)} disabled={busy} title={t("blog.fNotify")}
                    className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                    <Send className="w-3.5 h-3.5" />
                  </button>
                )}
                <span className={`rounded-full px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${p.visibility === "internal" ? "bg-slate-800 text-white" : "bg-sky-50 text-sky-700"}`}>
                  {p.visibility === "internal" ? t("blog.internal") : t("blog.public")}
                </span>
                <span className={`rounded-full px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${p.scheduled ? "bg-violet-50 text-violet-700" : p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {p.scheduled ? t("blog.scheduled") : p.status === "published" ? t("blog.published") : t("blog.draft")}
                </span>
                {p.scheduled && (p.author || "").startsWith("ai-batch") && (
                  <button data-testid={`admin-blog-regen-${p.post_id}`} onClick={() => regen(p)} disabled={busy} title={t("blog.regen")}
                    className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-violet-400 hover:text-violet-500 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
                <button data-testid={`admin-blog-edit-${p.post_id}`} onClick={() => startEdit(p)}
                  className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`admin-blog-del-${p.post_id}`} onClick={() => del(p)} disabled={busy}
                  className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assistente AI */}
      <div className="lg:col-span-2 rounded-3xl bg-[#1E293B] text-white p-6" data-testid="blog-ai-panel">
        <div className="flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
          <Sparkles className="w-4 h-4 text-[#FF6B35]" /> {t("blog.aiTitle")}
        </div>
        <div className="mt-3 flex flex-col md:flex-row gap-2">
          <input data-testid="blog-ai-topic" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
            placeholder={t("blog.aiTopicPh")}
            className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3 text-sm outline-none focus:border-[#FF6B35] placeholder:text-slate-500 transition-colors" />
          <button data-testid="blog-ai-draft" onClick={aiDraft} disabled={!!aiBusy || !aiTopic.trim()}
            className="rounded-full bg-[#FF6B35] px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {aiBusy === "draft" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {t("blog.aiDraft")}
          </button>
          <button data-testid="blog-ai-image" onClick={aiImage} disabled={!!aiBusy || !(form.title || aiTopic).trim()}
            className="rounded-full bg-white/10 border border-white/15 px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-white/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {aiBusy === "image" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {t("blog.aiImage")}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-3">
          <button data-testid="blog-ai-batch" onClick={aiBatchStart} disabled={!!aiBusy || batch?.status === "running"}
            className="rounded-full bg-emerald-500 px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {aiBusy === "batch" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {t("blog.aiBatch")}
          </button>
          {batch?.status === "running" && (
            <div data-testid="blog-ai-progress" className="flex items-center gap-2 text-sm text-slate-300">
              <Loader2 className="w-4 h-4 animate-spin text-[#FF6B35]" />
              {t("blog.aiBatchRunning").replace("{done}", batch.done || 0).replace("{total}", batch.total || 50)}
            </div>
          )}
          {batch?.status === "done" && (
            <div data-testid="blog-ai-done" className="text-sm text-emerald-400">{t("blog.aiBatchDone").replace("{total}", batch.total)}</div>
          )}
          {["paused_budget", "paused_error"].includes(batch?.status) && (
            <div data-testid="blog-ai-paused" className="text-sm text-amber-400">
              {t("blog.aiBatchPaused").replace("{done}", batch.done || 0).replace("{total}", batch.total)}
            </div>
          )}
          {["running", "paused_budget", "paused_error", "paused_daily"].includes(batch?.status) && (
            <button data-testid="blog-ai-stop" onClick={aiBatchStop} disabled={!!aiBusy}
              className="rounded-full bg-red-500/15 border border-red-400/40 text-red-300 px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
              {aiBusy === "stop" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />} {t("blog.aiStop")}
            </button>
          )}
          {blogAutoOn === false && (
            <p data-testid="blog-ai-auto-off" className="text-xs text-amber-400">{t("blog.aiAutoOff")}</p>
          )}
          <p className="text-xs text-slate-500">{t("blog.aiBatchHint")}</p>
        </div>
      </div>

      {/* editor */}
      <form onSubmit={save} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 space-y-4" data-testid="admin-blog-editor">
        <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#FF6B35]" /> {form.post_id ? t("blog.editPost") : t("blog.newPost")}
        </h3>
        <div>
          <label className={lbl}>{t("blog.fTitle")}</label>
          <input data-testid="blog-f-title" required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={lbl}>Slug</label>
          <input data-testid="blog-f-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={t("blog.slugHint")} className={`${inputCls} font-mono-t`} />
        </div>
        <div>
          <label className={lbl}>{t("blog.fImage")}</label>
          {form.image && (
            <img data-testid="blog-f-image-preview" src={imgSrc(form.image)} alt="" className="mb-2 w-full h-32 object-cover rounded-2xl border border-slate-100" />
          )}
          <div className="flex gap-2">
            <input data-testid="blog-f-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder={t("blog.imageUrlHint")} className={`${inputCls} flex-1 font-mono-t text-xs`} />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={upload} className="hidden" data-testid="blog-f-image-file" />
            <button type="button" data-testid="blog-f-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="shrink-0 rounded-2xl border border-slate-200 px-3 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors flex items-center gap-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {uploading ? t("blog.imageUploading") : t("blog.imageUpload")}
            </button>
          </div>
        </div>
        <div>
          <label className={lbl}>{t("blog.fExcerpt")}</label>
          <textarea data-testid="blog-f-excerpt" rows={2} maxLength={400} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={lbl}>{t("blog.fContent")}</label>
          <textarea data-testid="blog-f-content" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={lbl}>{t("blog.fCategories")}</label>
          <input data-testid="blog-f-categories" value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="guide, business, news" className={inputCls} />
          <p className="mt-1 text-xs text-slate-400">{t("blog.categoriesHint")}</p>
        </div>
        <div>
          <label className={lbl}>{t("blog.fTags")}</label>
          <input data-testid="blog-f-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="esim, viaggi, 5g" className={inputCls} />
          <p className="mt-1 text-xs text-slate-400">{t("blog.tagsHint")}</p>
        </div>
        <div>
          <label className={lbl}>{t("blog.fPublishAt")}</label>
          <input data-testid="blog-f-publish-at" type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} className={inputCls} />
          <p className="mt-1 text-xs text-slate-400">{t("blog.publishAtHint")}</p>
        </div>
        <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none">
          <input data-testid="blog-f-notify" type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })}
            className="w-4 h-4 accent-[#FF6B35]" />
          {t("blog.fNotify")}
        </label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className={lbl}>{t("blog.fLang")}</label>
            <select data-testid="blog-f-lang" value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className={inputCls}>
              {["it", "en", "es", "fr", "de", "pt", "nl", "pl", "tr", "ja", "zh"].map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>{t("blog.fVisibility")}</label>
            <select data-testid="blog-f-visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className={inputCls}>
              <option value="public">{t("blog.public")}</option>
              <option value="internal">{t("blog.internal")}</option>
            </select>
          </div>
          <div>
            <label className={lbl}>{t("blog.fStatus")}</label>
            <select data-testid="blog-f-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
              <option value="published">{t("blog.published")}</option>
              <option value="draft">{t("blog.draft")}</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="blog-f-save" type="submit" disabled={busy}
            className="flex-1 rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t("admin.save")}
          </button>
          {editing && (
            <button type="button" data-testid="blog-f-cancel" onClick={() => { setForm(EMPTY); setEditing(false); }}
              className="rounded-full border border-slate-200 px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-slate-400 transition-colors">
              {t("kyc.confirmNo")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
